import React, { FC, useEffect } from 'react'
import ApplicationStore from '../../../../GeneralApplication/Application/ApplicationStore'
import { ILoanPaymentPlanModel } from '@linqpal/models'
import { IDownPaymentDetails } from '@linqpal/models/src/types/routes'
import { StyleSheet, Text, View } from 'react-native'
import { Trans, useTranslation } from 'react-i18next'
import { BtButton, BtModal, BtPlainText } from '@linqpal/components/src/ui'
import { Invoice } from '../../../InvoicesTab/Invoice/InvoiceDetails'
import { frequency } from '@linqpal/models/src/dictionaries'
import { commonColors } from '@linqpal/common-frontend/src/theme'
import { PaymentPlansList } from './PaymentPlansList'
import { BtModalHeader } from '../../../../../ui/atoms/BtModalHeader'

interface IProps {
  invoices: Invoice[]
  paymentPlan: ILoanPaymentPlanModel | null
  onSelect: (
    paymentPlan: ILoanPaymentPlanModel,
    downPayment: IDownPaymentDetails | null,
  ) => void
  onContinue: () => void
  onClose?: () => void
}

interface ITitleProps {
  paymentPlans: ILoanPaymentPlanModel[]
}

interface IFooterProps {
  canContinue: boolean
  onContinue: () => void
}

export const PaymentPlanModal: FC<IProps> = ({
  invoices,
  paymentPlan,
  onSelect,
  onContinue,
  onClose,
}) => {
  // for backward compatibility only, remove dependencies from application store
  const { paymentPlans } = ApplicationStore

  const handleSelect = (
    paymentPlanDetails: ILoanPaymentPlanModel,
    downPaymentDetails: IDownPaymentDetails | null,
  ) => {
    onSelect(paymentPlanDetails, downPaymentDetails)
  }

  return (
    <BtModal
      visible={true}
      onClose={onClose}
      showDivider={false}
      customHeader={
        <BtModalHeader
          title={<Title paymentPlans={paymentPlans} />}
          onClose={onClose}
        />
      }
      footer={<Footer canContinue={!!paymentPlan} onContinue={onContinue} />}
    >
      <PaymentPlansList
        invoices={invoices}
        paymentPlan={paymentPlan}
        onSelect={handleSelect}
      />
    </BtModal>
  )
}

const Title: FC<ITitleProps> = ({ paymentPlans }) => {
  const { t } = useTranslation('application')

  const hasSinglePayment = paymentPlans?.some(
    (p) => p.frequency === frequency.SINGLE,
  )

  return (
    <View>
      <BtPlainText style={styles.title}>
        {t('CreditRequestPaymentPlan.mobileTitle')}
      </BtPlainText>

      {!hasSinglePayment && paymentPlans?.length ? (
        <BtPlainText>
          <Trans
            t={t}
            i18nKey="CreditRequestPaymentPlan.NoPaymentDueFirst30days"
            components={{
              styled: <Text style={styles.subtitleStyled} />,
              simple: <Text style={styles.subtitleSimple} />,
            }}
          />
        </BtPlainText>
      ) : null}
    </View>
  )
}

const Footer: FC<IFooterProps> = ({ canContinue, onContinue }) => {
  const { t } = useTranslation('global')

  const [processing, setProcessing] = React.useState(false)

  useEffect(() => {
    setProcessing(false)
  }, [])

  const handlePress = () => {
    setProcessing(true)
    onContinue()
  }

  return (
    <BtButton
      onPress={handlePress}
      style={styles.continueButton}
      testID="payment-plan-modal-continue-button"
      disabled={processing || !canContinue}
      loading={processing}
    >
      {t('DownPaymentModal.continue')}
    </BtButton>
  )
}

const styles = StyleSheet.create({
  title: {
    fontWeight: '700',
    fontSize: 24,
    lineHeight: 34,
    letterSpacing: -0.5,
    textAlign: 'left',
  },
  subtitleStyled: {
    color: commonColors.accentText,
    fontWeight: '500',
  },
  subtitleSimple: {
    fontWeight: '500',
    fontSize: 12,
    color: '#668598',
  },
  continueButton: {
    width: 150,
    alignSelf: 'flex-end',
  },
})
